import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Route, Routes, Navigate, NavLink, useLocation } from 'react-router-dom';
import ErrorBoundary from '../components/misc/ErrorBoundary';
import ThemeToggle from './ThemeToggle';
import CommandPalette from './components/CommandPalette';
import { cleanupToken } from '../common/auth';
import { rememberListSearch } from './lastList';
import Login from './pages/Login';
import routes from './routes';
import nav from './nav';
import {
  THEMES,
  getResolvedTheme,
  getStoredTheme,
  subscribeToSystemTheme
} from './theme';

const LOGO = 'https://static.wannasport.dk/vector/';

// The shortcut is not the same key everywhere, and printing ⌘K to somebody on
// Windows tells them to press something that does nothing. `userAgentData` is
// the current answer and `platform` the deprecated one that still works.
const IS_APPLE = typeof navigator !== 'undefined'
  && /mac|iphone|ipad/i.test(navigator.userAgentData?.platform || navigator.platform || '');
const SHORTCUT = IS_APPLE ? '\u2318K' : 'Ctrl K';

/**
 * Root of the rebuilt admin, mounted at `/next/*`.
 *
 * **Everything about this tree is opt-in.** It has its own layout, its own
 * stylesheet and its own routes; it imports nothing from the old shell except
 * `ErrorBoundary`, which is behaviour rather than appearance. The only existing
 * file it required a change to is `client.js`, and that change is three lines
 * that add a route — the old `Layout` still receives every other path exactly
 * as before.
 *
 * That isolation is the point, not fussiness: the old admin is what the rest of
 * the company uses, and this rebuild is a side project. It has to be impossible
 * for work in here to change what they see.
 *
 * `data-ui="next"` on the wrapper is what every token in
 * `next/styles/_tokens.scss` hangs off, so the design system simply does not
 * exist outside this subtree. `data-theme` sits on the same element rather than
 * on `<html>` for the same reason.
 *
 * The shell follows the prototype: a 252px rail on the left, a sticky top bar
 * with the breadcrumb and search, and the content column beneath it.
 *
 * On a phone that rail becomes a drawer. It has to: 252px of a 390px screen is
 * two thirds of the window spent on navigation, and the prototype has nothing to
 * say about small screens. The drawer is closed on every navigation, because a
 * menu that stays open over the page you just asked for is a menu you have to
 * dismiss twice.
 *
 * The top bar's search is live now, as ⌘K — see `components/CommandPalette`. It
 * needed no endpoint: the nav is a list this file already imports, and the
 * lookups behind it are the ones the filters use.
 *
 * The rail's user block shows whoever is signed in rather than the placeholder
 * the prototype drew. In a panel where every action is taken against live data
 * and several are irreversible, "you are signed in as somebody else's account"
 * is worth one line at the bottom of the screen.
 */
function NextRoot({ isLoggedIn, user }) {
  const [preference, setPreference] = useState(getStoredTheme);
  const [resolved, setResolved] = useState(() => getResolvedTheme());
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  // Any navigation closes the drawer, including one made from inside it.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  // Remember the search each list was left on, so the "← All signups" link on a
  // detail page can put it back. Done here rather than in eighteen list pages:
  // a route with no parameter is a list, and there is exactly one place that
  // sees every navigation.
  useEffect(() => {
    if (!location.pathname.startsWith('/next/')) return;
    const rest = location.pathname.slice('/next/'.length);
    // One segment and not a create form — `/next/signups`, not
    // `/next/signup/<id>` and not `/next/facility/new`.
    if (rest && !rest.includes('/')) {
      rememberListSearch(location.pathname, location.search);
    }
  }, [location.pathname, location.search]);

  // Escape closes it, as it does the dialog.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = event => { if (event.key === 'Escape') setNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  // ⌘K anywhere, and `/` when the caret is not already in a field — the second
  // is what makes it reachable one-handed from a list you are reading.
  useEffect(() => {
    const onKey = event => {
      const typing = /^(input|textarea|select)$/i.test(event.target?.tagName)
        || event.target?.isContentEditable;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(open => !open);
        return;
      }
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    setResolved(getResolvedTheme(preference));
  }, [preference]);

  // Only meaningful while following the system: an explicit choice wins.
  useEffect(() => {
    if (preference !== THEMES.SYSTEM) {
      return undefined;
    }

    return subscribeToSystemTheme(() => setResolved(getResolvedTheme(THEMES.SYSTEM)));
  }, [preference]);

  // Same single gate the old `Layout` uses: no user in the store, no admin.
  // Duplicating the check rather than reusing `Layout`'s is the price of not
  // sharing a shell, and it is one line.
  if (!isLoggedIn) {
    return (
      <div className="next-root" data-ui="next" data-theme={resolved}>
        <Login />
      </div>
    );
  }

  // Exact match for list pages; a detail route carries a parameter, so it is
  // matched on its own prefix instead.
  const current = routes.find(({ path }) => (
    path.includes(':')
      ? location.pathname.startsWith(`/next/${path.split('/:')[0]}/`)
      : location.pathname === `/next/${path}`
  ));

  // The store keeps the profile in the shape `_profiles.getUser` builds.
  const name = user?.UserName || user?.UserEmail || 'Admin';
  const email = user?.UserEmail || '';
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase() || 'WS';

  // The same two steps `client.js` takes when a token turns out to be bad:
  // drop it, then reload so the app re-reads the session from nothing.
  const signOut = () => {
    cleanupToken();
    window.location.assign('/next/home');
  };

  return (
    <div
      className={`next-root next-root--shell${navOpen ? ' next-root--nav-open' : ''}`}
      data-ui="next"
      data-theme={resolved}
    >
      {/* Only ever visible on a phone, where the rail is a drawer over the page. */}
      <button
        type="button"
        className="next-scrim"
        hidden={!navOpen}
        aria-label="Close the menu"
        onClick={() => setNavOpen(false)}
      />

      <aside className="next-rail" id="next-rail">
        <a className="next-rail__brand" href="/next/home">
          <img className="next-rail__mark" src={`${LOGO}logo-white.svg`} alt="" />
          <span className="next-rail__wordmark">
            <img src={`${LOGO}logotype-white.svg`} alt="WannaSport" />
            <span className="next-rail__eyebrow">Administration</span>
          </span>
        </a>

        <nav className="next-rail__nav">
          {nav.map(({ group, items }) => (
            <React.Fragment key={group || 'top'}>
              {group && <div className="next-rail__group">{group}</div>}
              {items.map(item => (item.to ? (
                <NavLink
                  key={item.label}
                  to={`/next/${item.to}`}
                  className={({ isActive }) => (
                    isActive ? 'next-rail__link next-rail__link--active' : 'next-rail__link'
                  )}
                >
                  <span className="next-rail__icon" aria-hidden>{item.icon}</span>
                  <span className="next-rail__label">{item.label}</span>
                </NavLink>
              ) : (
                // Not rebuilt yet: a plain anchor out to the old admin, so the
                // jump is a full page load and this tree's styles go with it.
                <a key={item.label} className="next-rail__link next-rail__link--old" href={item.href}>
                  <span className="next-rail__icon" aria-hidden>{item.icon}</span>
                  <span className="next-rail__label">{item.label}</span>
                  <span className="next-rail__out" aria-label="Opens the old admin">↗</span>
                </a>
              )))}
            </React.Fragment>
          ))}
        </nav>

        <div className="next-rail__user">
          <span className="next-rail__avatar" aria-hidden>{initials}</span>
          <span className="next-rail__who">
            <span className="next-rail__name" title={name}>{name}</span>
            <span className="next-rail__role" title={email}>{email || 'Signed in'}</span>
          </span>
          <button
            type="button"
            className="next-rail__signout"
            title="Sign out"
            aria-label={`Sign out${name === 'Admin' ? '' : ` ${name}`}`}
            onClick={signOut}
          >
            ⏻
          </button>
        </div>
      </aside>

      <main className="next-main">
        <header className="next-topbar">
          <button
            type="button"
            className="next-burger"
            aria-label={navOpen ? 'Close the menu' : 'Open the menu'}
            aria-expanded={navOpen}
            aria-controls="next-rail"
            onClick={() => setNavOpen(open => !open)}
          >
            <span aria-hidden>{navOpen ? '✕' : '☰'}</span>
          </button>

          <div className="next-crumb">
            <span>Admin</span>
            <span className="next-crumb__sep">/</span>
            <span className="next-crumb__here">{current ? current.label : 'Admin'}</span>
          </div>

          <div className="next-topbar__spacer" />

          {/*
            A button, not an input: what it opens is the field. Keeping the
            top bar's shape means the palette is discoverable by people who
            never learn the shortcut, and the shortcut is printed on it for
            the people who will.
          */}
          <button
            type="button"
            className="next-search"
            onClick={() => setPaletteOpen(true)}
          >
            <span className="next-search__icon" aria-hidden>⌕</span>
            <span className="next-search__text">Search a page, a name, or an id</span>
            <span className="next-search__key" aria-hidden>{SHORTCUT}</span>
          </button>

          <ThemeToggle preference={preference} onChange={setPreference} />
        </header>

        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

        <div className="next-content">
          <Routes>
            {routes.map(({ path, component: PageComponent }) => (
              <Route
                key={path}
                path={path}
                element={(
                  <ErrorBoundary>
                    <PageComponent />
                  </ErrorBoundary>
                )}
              />
            ))}
            <Route path="*" element={<Navigate to="/next/home" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

NextRoot.propTypes = {
  isLoggedIn: PropTypes.bool,
  user: PropTypes.object
};

export default connect(({ user }) => ({ isLoggedIn: !!user, user }), {})(NextRoot);
